import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Button, Alert, Stack, CircularProgress,
  InputAdornment, IconButton, Divider,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import Colors from '../../components/ui/Colors';
import { validateActivationToken, activateAccount, resendActivation } from '../../utilityFunctions/OnboardingUtil';
import { bootstrapTenantSession } from '../../utilityFunctions/TenantUtil';

const MIN_PASSWORD_LEN = 8;

// PUBLIC (no-login) activation surface, mounted at /onboarding/activate.
// The activation email links here with ?token=<activationToken>. It validates
// the token, collects a new password, and on success replays exactly what
// Login.js does with a JwtResponse — bootstrapTenantSession + lift auth state
// via props.userdata — then navigates into the (now authenticated) wizard.
export default function ActivateAccountPage(props) {
  const { userdata, updateNavBarEnabled } = props;
  const history = useHistory();

  const search = (props.location && props.location.search) || window.location.search || '';
  const activationToken = new URLSearchParams(search).get('token');

  const [validating, setValidating] = React.useState(true);
  const [valid, setValid] = React.useState(false);
  const [account, setAccount] = React.useState({ email: '', orgName: '' });

  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [showPwd, setShowPwd] = React.useState(false);
  const [fieldError, setFieldError] = React.useState('');
  const [submitError, setSubmitError] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const [resendEmail, setResendEmail] = React.useState('');
  const [resendMsg, setResendMsg] = React.useState('');
  const [resending, setResending] = React.useState(false);

  // --- Validate the activation token on mount ------------------------------
  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!activationToken) { setValidating(false); setValid(false); return; }
      const res = await validateActivationToken(activationToken);
      if (cancelled) return;
      if (res.ok && res.data && res.data.valid) {
        setValid(true);
        setAccount({ email: res.data.email || '', orgName: res.data.orgName || '' });
      } else {
        setValid(false);
      }
      setValidating(false);
    };
    run();
    return () => { cancelled = true; };
  }, [activationToken]);

  const validatePassword = () => {
    if (password.length < MIN_PASSWORD_LEN) {
      setFieldError(`Use at least ${MIN_PASSWORD_LEN} characters.`);
      return false;
    }
    if (password !== confirm) {
      setFieldError('Passwords do not match.');
      return false;
    }
    setFieldError('');
    return true;
  };

  // Replicates Login.js: fold the JwtResponse into app auth state after
  // resolving the tenant session, so the app flips to the authenticated shell.
  const autoLogin = async (data) => {
    const tenantSession = await bootstrapTenantSession(data.accessToken);
    userdata({
      accountId: data.username,
      ilpBearerToken: data.ilpBearerToken,
      totpToken: data.totpToken,
      jwtToken: data.accessToken,
      profileCompleted: data.profileCompleted,
      roles: data.roles,
      firstName: data.firstName,
      ...tenantSession,
    });
    if (typeof updateNavBarEnabled === 'function') await updateNavBarEnabled(true);
    // Setting auth state flips <Auth> from public Routes to AuthedRoutes, where
    // /onboarding/self-serve resolves to the authenticated SelfServeOnboardingWizard.
    history.push('/onboarding/self-serve');
  };

  const handleActivate = async () => {
    setSubmitError('');
    if (!validatePassword()) return;
    setBusy(true);
    const res = await activateAccount(activationToken, password);
    if (res.ok && res.data && res.data.accessToken) {
      await autoLogin(res.data);
      return; // navigated away
    }
    setBusy(false);
    if (res.status === 400 || res.status === 410) {
      setValid(false); // link invalid/expired — drop into the resend state
      return;
    }
    setSubmitError(res.error || 'We could not activate your account. Please try again.');
  };

  const handleResend = async () => {
    if (!resendEmail.trim()) { setResendMsg('Enter your email to resend the activation link.'); return; }
    setResending(true);
    setResendMsg('');
    await resendActivation(resendEmail.trim().toLowerCase());
    setResending(false);
    setResendMsg(`If ${resendEmail.trim().toLowerCase()} is on file, a new activation link is on its way.`);
  };

  const shell = (children) => (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 560, mx: 'auto', width: '100%', boxSizing: 'border-box' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <Box component="img" src="/images/logo_datalove.png" alt="Datalove" sx={{ height: 40, width: 'auto' }} />
      </Stack>
      <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 2 }}>{children}</Paper>
    </Box>
  );

  // --- Loading -------------------------------------------------------------
  if (validating) {
    return shell(
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
        <CircularProgress size={20} />
        <Typography color="text.secondary">Checking your activation link…</Typography>
      </Stack>,
    );
  }

  // --- Invalid / expired link → resend affordance --------------------------
  if (!valid) {
    return shell(
      <Stack spacing={2.5} sx={{ py: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <ErrorOutlineRoundedIcon sx={{ fontSize: 40, color: Colors.third }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: Colors.primary }}>
            This link is invalid or expired
          </Typography>
        </Stack>
        <Typography color="text.secondary">
          Activation links expire for your security. Enter your email and we&apos;ll send a fresh one.
        </Typography>
        <TextField
          label="Admin email"
          type="email"
          value={resendEmail}
          onChange={(e) => setResendEmail(e.target.value)}
          disabled={resending}
          fullWidth
        />
        <Button
          variant="contained"
          onClick={handleResend}
          disabled={resending}
          startIcon={resending ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ alignSelf: 'flex-start', backgroundColor: Colors.primary, '&:hover': { backgroundColor: Colors.primary } }}
        >
          {resending ? 'Sending…' : 'Resend activation email'}
        </Button>
        {resendMsg && <Alert severity="info">{resendMsg}</Alert>}
      </Stack>,
    );
  }

  // --- Set-password form ----------------------------------------------------
  return shell(
    <Stack spacing={2.5}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <LockResetRoundedIcon sx={{ fontSize: 36, color: Colors.secondary }} />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: Colors.primary }}>
            Set your password
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Finish activating your admin account.
          </Typography>
        </Box>
      </Stack>

      <Divider />

      <Stack spacing={0.5}>
        {account.orgName && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography color="text.secondary">Organization</Typography>
            <Typography sx={{ fontWeight: 600 }}>{account.orgName}</Typography>
          </Box>
        )}
        {account.email && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography color="text.secondary">Email</Typography>
            <Typography sx={{ fontWeight: 600 }}>{account.email}</Typography>
          </Box>
        )}
      </Stack>

      {submitError && <Alert severity="error">{submitError}</Alert>}

      <TextField
        label="New password"
        type={showPwd ? 'text' : 'password'}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={!!fieldError}
        helperText={fieldError || `At least ${MIN_PASSWORD_LEN} characters.`}
        disabled={busy}
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPwd((s) => !s)} edge="end">
                {showPwd ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <TextField
        label="Confirm password"
        type={showPwd ? 'text' : 'password'}
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        error={!!fieldError && password !== confirm}
        disabled={busy}
        fullWidth
      />

      <Button
        variant="contained"
        onClick={handleActivate}
        disabled={busy || !password || !confirm}
        startIcon={busy ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{ alignSelf: 'flex-start', backgroundColor: Colors.primary, '&:hover': { backgroundColor: Colors.primary } }}
      >
        {busy ? 'Activating…' : 'Activate & sign in'}
      </Button>
    </Stack>,
  );
}
